import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Users, ChalkboardTeacher, Money, CalendarCheck, Cake, Wallet,
  Bank, Coins, ArrowsClockwise, CheckCircle, Hourglass
} from '@phosphor-icons/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { toast } from 'sonner';
import { salariesApi } from '../lib/api';
import PushToggleCard from '../components/dashboard/PushToggleCard';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)) + ' FCFA';

const PIE_COLORS = ['#0052FF', '#10B981', '#F59E0B'];

const HrDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/hr/dashboard`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const markCompleted = async (id) => {
    setMarkingId(id);
    try {
      await salariesApi.update(id, {
        status: 'completed',
        transfer_date: new Date().toISOString().slice(0, 10),
      });
      toast.success('Virement marqué comme effectué');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setMarkingId(null); }
  };

  if (loading) return <p className="p-8 text-sm">Chargement…</p>;
  if (!data) return <p className="p-8 text-sm text-red-600">Erreur de chargement</p>;

  const payrollPct = data.expected_payroll > 0
    ? Math.round((data.paid_this_month / data.expected_payroll) * 100)
    : 0;

  const breakdown = data.method_breakdown || {};
  const pieData = [
    { name: 'Virements', value: breakdown.transfer_total || 0, count: breakdown.transfer_count || 0 },
    { name: 'Espèces', value: breakdown.cash_total || 0, count: breakdown.cash_count || 0 },
    { name: 'Autres', value: breakdown.other_total || 0, count: breakdown.other_count || 0 },
  ].filter(d => d.value > 0);

  const pending = data.pending_transfers || [];

  return (
    <div className="space-y-6" data-testid="hr-dashboard">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={28} className="text-[#0052FF]" weight="duotone" />
          <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">Tableau de bord — RH</h1>
        </div>
        <Button onClick={load} variant="outline" data-testid="hr-refresh">
          <ArrowsClockwise size={14} className="mr-1" /> Rafraîchir
        </Button>
      </div>

      {/* Push notifications toggle */}
      <PushToggleCard />

      {/* KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        {/* Direction group */}
        <Card className="border border-[#E2E8F0] shadow-none md:col-span-2" data-testid="kpi-direction">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[#64748B]">
                  <Users size={18} />
                  <span className="text-xs uppercase">Direction</span>
                </div>
                <p className="font-outfit text-2xl font-bold mt-1" data-testid="hr-kpi-direction">
                  {data.direction_count || 0}
                </p>
                <div className="mt-2 space-y-0.5 text-xs font-manrope text-[#475569]">
                  <div className="flex justify-between gap-3"><span>Personnel direction</span><span className="font-bold">{data.staff_breakdown?.direction || 0}</span></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pédagogique group (teachers + educators) */}
        <Card className="border border-[#E2E8F0] shadow-none md:col-span-2" data-testid="kpi-pedagogical">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[#64748B]">
                  <ChalkboardTeacher size={18} />
                  <span className="text-xs uppercase">Pédagogique</span>
                </div>
                <p className="font-outfit text-2xl font-bold mt-1" data-testid="hr-kpi-pedagogical">
                  {data.pedagogical_count || 0}
                </p>
                <div className="mt-2 space-y-0.5 text-xs font-manrope text-[#475569]">
                  <div className="flex justify-between gap-3"><span>Enseignants</span><span className="font-bold">{data.staff_breakdown?.teachers || 0}</span></div>
                  <div className="flex justify-between gap-3"><span>Éducateurs</span><span className="font-bold">{data.staff_breakdown?.educator || 0}</span></div>
                </div>
              </div>
              {data.pedagogical_count > 0 && (
                <div style={{ width: 90, height: 90 }} data-testid="ped-mini-pie">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Enseignants', value: data.staff_breakdown?.teachers || 0 },
                          { name: 'Éducateurs', value: data.staff_breakdown?.educator || 0 },
                        ].filter(d => d.value > 0)}
                        dataKey="value" innerRadius={22} outerRadius={42} paddingAngle={2}
                      >
                        <Cell fill="#0052FF" />
                        <Cell fill="#6366F1" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Services group (canteen + transport + cleaning) */}
        <Card className="border border-[#E2E8F0] shadow-none md:col-span-2" data-testid="kpi-services">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 text-[#64748B]">
                  <Users size={18} />
                  <span className="text-xs uppercase">Services</span>
                </div>
                <p className="font-outfit text-2xl font-bold mt-1" data-testid="hr-kpi-services">
                  {data.services_count || 0}
                </p>
                <div className="mt-2 space-y-0.5 text-xs font-manrope text-[#475569]">
                  <div className="flex justify-between gap-3"><span>Cantine</span><span className="font-bold">{data.staff_breakdown?.canteen || 0}</span></div>
                  <div className="flex justify-between gap-3"><span>Transport</span><span className="font-bold">{data.staff_breakdown?.transport || 0}</span></div>
                  <div className="flex justify-between gap-3"><span>Ménage</span><span className="font-bold">{data.staff_breakdown?.cleaning || 0}</span></div>
                </div>
              </div>
              {data.services_count > 0 && (
                <div style={{ width: 90, height: 90 }} data-testid="svc-mini-pie">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Cantine', value: data.staff_breakdown?.canteen || 0 },
                          { name: 'Transport', value: data.staff_breakdown?.transport || 0 },
                          { name: 'Ménage', value: data.staff_breakdown?.cleaning || 0 },
                        ].filter(d => d.value > 0)}
                        dataKey="value" innerRadius={22} outerRadius={42} paddingAngle={2}
                      >
                        <Cell fill="#10B981" />
                        <Cell fill="#F59E0B" />
                        <Cell fill="#A855F7" />
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B]"><CalendarCheck size={18} /><span className="text-xs uppercase">Présences aujourd&apos;hui</span></div>
            <p className="font-outfit text-2xl font-bold mt-1" data-testid="hr-kpi-attendance">{data.attendance_today}</p>
          </CardContent>
        </Card>
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[#64748B]"><Cake size={18} /><span className="text-xs uppercase">Anniversaires</span></div>
            <p className="font-outfit text-2xl font-bold mt-1" data-testid="hr-kpi-bdays">{(data.birthdays_this_month || []).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Payroll progress */}
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader>
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Wallet size={18} className="text-[#0052FF]" /> Masse salariale — mois courant
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div><p className="text-xs text-[#64748B]">Prévue</p><p className="font-bold">{fmt(data.expected_payroll)}</p></div>
            <div><p className="text-xs text-[#64748B]">Payée</p><p className="font-bold text-green-600">{fmt(data.paid_this_month)}</p></div>
            <div><p className="text-xs text-[#64748B]">Restante</p><p className="font-bold text-red-600">{fmt(data.outstanding_payroll)}</p></div>
          </div>
          <div className="w-full h-3 bg-[#F1F5F9] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#0052FF] to-[#10B981]" style={{ width: `${payrollPct}%` }} data-testid="hr-payroll-bar" />
          </div>
          <p className="text-xs text-[#64748B] text-right">{payrollPct}% versé · {data.paid_count} paiement(s)</p>
        </CardContent>
      </Card>

      {/* Virements widget row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Transfer / Cash / Other breakdown (mini stats) */}
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              <Bank size={18} className="text-indigo-600" /> Répartition — mois courant
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm" data-testid="breakdown-transfer">
              <span className="flex items-center gap-2"><Bank size={14} className="text-[#0052FF]" /> Virements</span>
              <div className="text-right">
                <span className="font-bold">{fmt(breakdown.transfer_total)}</span>
                <span className="block text-xs text-[#64748B]">{breakdown.transfer_count || 0} versement(s)</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm" data-testid="breakdown-cash">
              <span className="flex items-center gap-2"><Coins size={14} className="text-emerald-600" /> Espèces</span>
              <div className="text-right">
                <span className="font-bold">{fmt(breakdown.cash_total)}</span>
                <span className="block text-xs text-[#64748B]">{breakdown.cash_count || 0} versement(s)</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm" data-testid="breakdown-other">
              <span className="flex items-center gap-2"><Money size={14} className="text-amber-600" /> Autres</span>
              <div className="text-right">
                <span className="font-bold">{fmt(breakdown.other_total)}</span>
                <span className="block text-xs text-[#64748B]">{breakdown.other_count || 0} versement(s)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pie chart */}
        <Card className="border border-[#E2E8F0] shadow-none lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="font-outfit text-base flex items-center gap-2">
              Virements vs Espèces — mois courant
            </CardTitle>
          </CardHeader>
          <CardContent>
            {pieData.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-8">
                Aucun versement enregistré ce mois.
              </p>
            ) : (
              <div style={{ width: '100%', height: 220 }} data-testid="hr-pie-chart">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" outerRadius={80} label={(e) => `${e.name}: ${Math.round(e.percent * 100)}%`}>
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pending transfers widget */}
      <Card className="border border-amber-200 bg-amber-50/30 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Hourglass size={18} className="text-amber-600" />
            Virements en attente
            <span className="text-xs font-manrope text-[#64748B] ml-2">
              ({data.pending_transfers_count || 0} · total {fmt(data.pending_transfers_total)})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {pending.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Aucun virement en attente. ✅</p>
          ) : (
            <ul className="space-y-2" data-testid="pending-transfers-list">
              {pending.map(t => (
                <li
                  key={t.id}
                  className="flex items-center justify-between bg-white rounded-md border border-[#E2E8F0] p-3"
                  data-testid={`pending-${t.id}`}
                >
                  <div>
                    <p className="font-manrope font-semibold text-sm">{t.staff_name}</p>
                    <p className="text-xs text-[#64748B]">
                      {t.month} {t.year} · {fmt(t.amount)}
                      {t.transfer_reference && <span className="ml-2 font-mono text-indigo-700">[{t.transfer_reference}]</span>}
                    </p>
                    {t.notes && <p className="text-xs text-[#64748B] italic mt-0.5">{t.notes}</p>}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => markCompleted(t.id)}
                    disabled={markingId === t.id}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid={`mark-completed-${t.id}`}
                  >
                    <CheckCircle size={14} className="mr-1" />
                    {markingId === t.id ? 'Mise à jour…' : 'Marquer effectué'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Birthdays + Recent salaries */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader><CardTitle className="font-outfit text-base flex items-center gap-2"><Cake size={18} className="text-amber-500" /> Anniversaires du mois</CardTitle></CardHeader>
          <CardContent>
            {(data.birthdays_this_month || []).length === 0
              ? <p className="text-sm text-[#94A3B8]">Aucun</p>
              : (
                <ul className="space-y-1 text-sm">
                  {data.birthdays_this_month.map(b => (
                    <li key={b.id} className="flex justify-between border-b border-[#F1F5F9] pb-1 last:border-0" data-testid={`hr-bday-${b.id}`}>
                      <span>{b.name} <span className="text-xs text-[#64748B]">({b.kind})</span></span>
                      <span className="text-xs text-[#64748B]">{b.birthdate}</span>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>

        <Card className="border border-[#E2E8F0] shadow-none">
          <CardHeader><CardTitle className="font-outfit text-base flex items-center gap-2"><Money size={18} className="text-green-600" /> Derniers salaires versés</CardTitle></CardHeader>
          <CardContent>
            {(data.recent_salaries || []).length === 0
              ? <p className="text-sm text-[#94A3B8]">Aucun versement</p>
              : (
                <ul className="space-y-1 text-sm">
                  {data.recent_salaries.map(s => (
                    <li key={s.id} className="flex justify-between border-b border-[#F1F5F9] pb-1 last:border-0">
                      <span>
                        {s.staff_name}
                        <span className="text-xs text-[#64748B] ml-1">{s.month}/{s.year}</span>
                        {s.payment_method === 'transfer' && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-indigo-700">
                            <Bank size={12} /> {s.transfer_reference || 'virement'}
                          </span>
                        )}
                        {s.status === 'pending' && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-700">
                            <Hourglass size={12} /> en attente
                          </span>
                        )}
                      </span>
                      <span className="font-medium">{fmt(s.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HrDashboard;
